import { useEffect, useState } from "react";
import { T } from "react-translator-component";
import { Box, Paper, TableContainer, TextField, Typography } from "@mui/material";
import { useAppDispatch } from "../../redux/store";
import LoadingModal from "../../components/LoadingModal";
import Separator from "../../components/Separator";
import { colors } from "../../common/colors";
import "../../App.css";
import AdminPageFrame from "../components/AdminPageFrame";
import DropDown from "../components/DropDown";
import FetchButton from "../components/FetchButton";
import PostCard from "../components/post/PostCard";
import HeaderRejected from "../components/post/postCard/HeaderRejected";
import ReadMore from "../components/ReadMore";
import { setErrorObj } from "../redux/errorReducer";
import apiServices from "../services/apiServices";

export default function RejectedPostList() {
  const dispatch = useAppDispatch();

  /* 検索項目 */
  const sortOptions = [
    {value: "desc", name: T("mng_rejected_posts_sort_by_new")},
    {value: "asc", name: T("mng_rejected_posts_sort_by_old")},
  ]

  const [query, setQuery] = useState<string>("");
  const [sortOption, setSortOption] = useState<"asc" | "desc">("desc");

  /* API関連 */
  const [lastParams, setLastParams] = useState<any>();
  const [posts, setPosts] = useState<any[]>([]);
  const [pagination, setPagination] = useState<any>({});
  const [negativeWordList, setNegativeWordList] = useState<string[]>([]);

  /* 画面制御 */
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    getNegativeWords();

    const params = createParams();
    getPosts(params);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createParams = () => (
    {
      term: query,
      order: sortOption,
      per_page: 12,
      page: 1
    }
  );

  /* API関連 */
  const getNegativeWords = async () => {
    try {
      const res = await apiServices.getNegativeWords();
      setNegativeWordList(res.negative_words.map((item: any) => item.word));
    } catch (error: any) {
      dispatch(setErrorObj(error));
    }
  }

  const getPosts = async (params: any) => {
    const _p = { ...params, sort_type: "created_at" };

    try {
      const res = await apiServices.getRejectedPosts(_p);
      if (res.pagination.current_page > 1) {
        setPosts((prev: any[]) => [...prev, ...res.rejected_posts]);
      } else {
        setPosts(res.rejected_posts);
      }
      setPagination(res.pagination);
      setLastParams(_p);
    } catch (error: any) {
      dispatch(setErrorObj(error));
    }
  };

  const handleSearch = async () => {
    setIsLoading(true);

    const params = createParams();
    await getPosts(params);

    setIsLoading(false);

    return false;
  }

  /* イベント */
  const handleClickTag = (tag: string) => {
    setQuery(tag);
  }

  const loadMore = () => {
    if (!pagination?.next_page || pagination?.next_page < 2) return

    const params = { ...lastParams, page: pagination.next_page};
    getPosts(params);
  };

  return (
    <AdminPageFrame>
      <LoadingModal visible={isLoading} />

      <TableContainer component={Paper} sx={styles.search.container}>
        <Typography sx={styles.search.title}>{T("mng_search_area_title")}</Typography>
        <Box sx={styles.search.inputBox}>
          <TextField
            placeholder={`${T("mng_rejected_posts_search_query_placeholder")}`}
            value={query}
            onChange={(e: any) => setQuery(e.target.value)}
            fullWidth hiddenLabel variant="outlined"
          />
          <Typography variant="caption">{T("mng_rejected_posts_search_query_note")}</Typography>

          <Box sx={styles.search.bottom}>
            <DropDown
              title={T("mng_sort")}
              value={sortOption}
              options={sortOptions}
              onChange={(e: any) => setSortOption(e.target.value)}
              sx={styles.search.sort}
            />
            <FetchButton
              title={T('mng_search')}
              onSubmit={handleSearch}
            />
          </Box>
        </Box>
      </TableContainer>

      <TableContainer component={Paper} sx={styles.data.container}>
        <Typography sx={styles.data.title}>{T("mng_negative_words_rejected_posts")}</Typography>

        {posts.length > 0 && <Separator color={colors.farm_gray100} />}

        <div style={styles.data.list}>
          {posts.map((post: any, index: number) => (
            <PostCard
              post={post}
              header={<HeaderRejected post={post} highlightWords={negativeWordList} />}
              highlightWords={negativeWordList}
              onClickTag={handleClickTag}
              key={index}
            />
          ))}
        </div>

        <ReadMore open={pagination.next_page > 0} sx={styles.data.paginate} onClick={loadMore} />
      </TableContainer>
    </AdminPageFrame>
  );
}

const styles = {
  search: {
    container: {
      marginBottom: "10px",
      padding: "20px",
    },
    title: {
      paddingY: 2,
    },
    inputBox: {
      margin: "10px 20px"
    },
    sort: {
      minWidth: "14em"
    },
    bottom: {
      mt: 3,
      display: "flex",
      justifyContent: "space-between"
    }
  },
  data: {
    container: {
      paddingTop: 0
    },
    title: {
      paddingX: 4,
      lineHeight: "120px",
      fontWeight: 700,
      fontSize: 16,
    },
    list: {
      display: "flex",
      flexWrap: "wrap" as const,
      gap: 20
    },
    paginate: {
      width: "100%",
      display: "flex",
      flexDirection: "row" as const,
      justifyContent: "center",
      alignItems: "center"
    }
  }
}